import loadArcGIS from './utils/loadArcGIS';
import { customViewMapStyles, addLegendStylesAfterRender } from './utils/customViewMapStyles';
import { locationSearch } from './mapSearch';
import { buildCardsFromLayers, updateVisibleCards } from '../ProjectCard';

class InteractiveMap {
  constructor(options = {}) {
    this.containerId = 'viewDiv';
    this.apiKey = options.element.dataset.apiKey;
    this.options = Object.assign(
      options,
    );
  }

  async init() {
    try {
      await loadArcGIS();
      await this.initMap();
    } catch (loadError) {
      console.error('Fehler beim Laden der ArcGIS API:', loadError);
    }
  }

  async initMap() {
    if (!window.require) {
      console.error('ArcGIS API wurde nicht geladen.');
      return;
    }

    window.require(
      [
        'esri/WebMap',
        'esri/views/MapView',
        'esri/widgets/Home',
        'esri/rest/locator',
        'esri/geometry/Circle',
        'esri/Graphic',
        'esri/widgets/Legend',
        'esri/widgets/Expand',
      ],
      (WebMap, MapView, Home, locator, Circle, Graphic, Legend, Expand) => {
        this.WebMap = WebMap;
        this.MapView = MapView;
        this.Home = Home;
        this.geocoder = locator;
        this.geocodeUrl = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';
        this.Circle = Circle;
        this.Graphic = Graphic;
        this.createMap();
        this.Legend = Legend;
        this.Expand = Expand;
      },
    );
  }

  async createMap() {
    const germanyExtent = {
      type: 'extent',
      xmin: 653000,
      ymin: 5970000,
      xmax: 1683000,
      ymax: 7370000,
      spatialReference: { wkid: 3857 },
    };

    const webMap = await new this.WebMap({
      portalItem: {
        id: this.apiKey,
      },
    });

    const view = await new this.MapView({
      container: this.containerId,
      map: webMap,
      zoom: 5,
      extent: germanyExtent,
      constraints: {
        minZoom: 5,
        maxZoom: 16,
        minScale: 9000,
        geometry: germanyExtent,
      },
      ...this.options,
    });

    this.map = webMap;
    this.view = view;

    const home = new this.Home({
      view,
      viewpoint: {
        targetGeometry: germanyExtent,
      },
    });

    view.ui.add(home, 'top-right');
    home.id = 'home';
    view.ui.move('zoom', 'top-right');
    view.ui.remove('attribution');

    this.view.when(async () => {
      await this.initProjectCards(this.map, view);
      await locationSearch(view, this.geocoder, this.geocodeUrl, this.Circle, this.Graphic);
      customViewMapStyles(view);
      /**
       * Needs to be placed here in case of waiting for legend container
       */
      const legend = new this.Legend({ view });
      const legendExpand = new this.Expand({
        view,
        content: legend,
        expandTooltip: 'Legende',
        expandIcon: 'legend',
        collapseIcon: 'chevrons-right',
        mode: 'floating',
      });

      view.ui.add(legendExpand, 'top-right');
      legendExpand.id = 'legend';

      addLegendStylesAfterRender(view);
    });

    view.watch('updating', async (updating) => {
      if (!updating) {
        await this.updateLayerVisibility();
      }
    });

    this.view.watch('scale', (scale) => {
      if (scale < 9000) {
        this.view.scale = 9000;
      }
    });
  }

  async updateLayerVisibility() {
    const cardListCount = document.querySelector('.org--interactive-map__count-results');
    const cardListCountNone = document.querySelector('.org--interactive-map__count-results-none');
    const cardListCountText = document.querySelector('.org--interactive-map__count-results-found-text');
    cardListCount.classList.add('loading');
    cardListCountNone.classList.add('loading');
    cardListCountText.classList.add('loading');

    const visibilityResults = await this.checkVisibleParentLayers().finally(() => {
      cardListCount.classList.remove('loading');
      cardListCountNone.classList.remove('loading');
      cardListCountText.classList.remove('loading');
    });

    updateVisibleCards(visibilityResults);
  }

  async checkVisibleParentLayers() {
    const results = [];

    for (const parent of this.map.layers.toArray()) {
      if (parent.type === 'group') {
        const sublayers = parent.layers.items;
        let anyVisible = false;

        for (const sub of sublayers) {
          if (sub.type !== 'feature' || !sub.visible) {
            /* eslint-disable  no-continue */
            continue;
          }

          const query = sub.createQuery();
          query.geometry = this.view.extent;
          query.where = '1=1';
          query.outSpatialReference = sub.spatialReference;

          /* eslint-disable  no-await-in-loop */
          const count = await sub.queryFeatureCount(query)
            .then(newCount => newCount)
            .catch((error) => {
              if (error.name === 'AbortError' || error.message.includes('aborted') || error.message.includes('requests')) {
                return;
              }

              console.warn(error.message);
            });

          if (count > 0) {
            anyVisible = true;
            break;
          }
        }

        results.push({
          id: parent.id,
          title: parent.title,
          visible: anyVisible,
        });
      }
    }

    return results;
  }

  async initProjectCards(map, view) {
    if (map && view) {
      await buildCardsFromLayers(map, view);
    }
  }
}

export default InteractiveMap;
